import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Camera, ZoomIn, ZoomOut, X, Columns2,
  ChevronLeft, ChevronRight, Maximize2, ArrowLeftRight,
} from "lucide-react";

function FullscreenSideCompare({ photoLeft, photoRight, onClose, onSwap }) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)));
      if (e.key === "-") setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const panels = [
    { photo: photoLeft, title: "Photo de gauche", accent: "border-slate-500" },
    { photo: photoRight, title: "Photo de droite", accent: "border-blue-500" },
  ];

  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" role="dialog" aria-modal="true" aria-label="Comparatif plein écran">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-950/95 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Columns2 className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="text-sm font-medium text-white truncate">Comparatif plein écran</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-slate-300 hover:text-white hover:bg-white/10"
            onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}
            disabled={zoom <= 1}
            title="Zoom arrière"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-slate-400 w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-slate-300 hover:text-white hover:bg-white/10"
            onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
            disabled={zoom >= 3}
            title="Zoom avant"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          {onSwap && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 text-slate-300 hover:text-white hover:bg-white/10 rounded-full text-xs hidden sm:flex"
              onClick={onSwap}
            >
              <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Inverser
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-slate-300 hover:text-white hover:bg-white/10"
            onClick={onClose}
            title="Fermer (Échap)"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col sm:flex-row min-h-0">
        {panels.map(({ photo, title, accent }, idx) => (
          <div
            key={photo.photo_id}
            className={`flex-1 flex flex-col min-h-0 min-w-0 ${idx === 0 ? "sm:border-r border-white/10" : "border-t sm:border-t-0 border-white/10"}`}
          >
            <div className="flex-1 overflow-auto flex items-center justify-center p-3 sm:p-6 bg-black">
              <img
                src={photo.image_url}
                alt={photo.caption || title}
                className="max-w-full max-h-full object-contain transition-transform duration-150 select-none"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
                onClick={() => setZoom((z) => (z === 1 ? 1.75 : 1))}
              />
            </div>
            <div className={`shrink-0 px-4 py-3 bg-slate-950/90 border-t-2 ${accent}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{title}</p>
                  <p className="text-xs text-slate-400">{formatPhotoDate(photo.created_at)}</p>
                  {photo.caption && (
                    <p className="text-xs text-slate-500 truncate mt-0.5">{photo.caption}</p>
                  )}
                </div>
                <Badge variant="outline" className="text-[10px] border-white/20 text-slate-300 shrink-0">
                  {idx === 0 ? "Gauche" : "Droite"}
                </Badge>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-[11px] text-slate-600 py-2 bg-black shrink-0">
        Clic sur une photo pour zoomer · Échap pour fermer
      </p>
    </div>
  );
}

function formatPhotoDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function PhotoLightbox({ photo, photos, onClose }) {
  const [zoom, setZoom] = useState(1);
  const index = photos.findIndex((p) => p.photo_id === photo.photo_id);

  useEffect(() => {
    setZoom(1);
  }, [photo.photo_id]);

  const go = (dir) => {
    const next = photos[index + dir];
    if (next) onClose(next);
  };

  return (
    <Dialog open onOpenChange={() => onClose(null)}>
      <DialogContent className="max-w-[min(96vw,1100px)] w-full p-0 gap-0 bg-slate-950 border-slate-800 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/80">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {photo.caption || "Photo de progression"}
            </p>
            <p className="text-xs text-slate-400">{formatPhotoDate(photo.created_at)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}
              disabled={zoom <= 1}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-xs text-slate-400 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={() => setZoom((z) => Math.min(3, +(z + 0.5).toFixed(1)))}
              disabled={zoom >= 3}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={() => onClose(null)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="relative flex items-center justify-center min-h-[50vh] max-h-[78vh] overflow-auto bg-black p-4">
          {index > 0 && (
            <button
              type="button"
              onClick={() => go(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
              aria-label="Photo précédente"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <img
            src={photo.image_url}
            alt={photo.caption || "Photo"}
            className="max-w-full max-h-[72vh] object-contain transition-transform duration-200 cursor-zoom-in"
            style={{ transform: `scale(${zoom})` }}
            onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
            draggable={false}
          />
          {index < photos.length - 1 && (
            <button
              type="button"
              onClick={() => go(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
              aria-label="Photo suivante"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="text-center text-[11px] text-slate-500 py-2">
          Cliquez sur l&apos;image pour zoomer · Flèches pour naviguer
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default function PhotoEvolutionPanel({
  photos = [],
  showDelete = false,
  onDelete,
  emptyMessage = "Aucune photo partagée",
}) {
  const sorted = useMemo(
    () => [...photos].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [photos],
  );

  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [photoLeft, setPhotoLeft] = useState(null);
  const [photoRight, setPhotoRight] = useState(null);
  const [fullscreenCompare, setFullscreenCompare] = useState(false);

  const cancelCompareMode = () => {
    setCompareMode(false);
    setPhotoLeft(null);
    setPhotoRight(null);
  };

  const startCompareMode = () => {
    setLightboxPhoto(null);
    setCompareMode(true);
    setPhotoLeft(null);
    setPhotoRight(null);
    setFullscreenCompare(false);
  };

  const handleGalleryClick = (photo) => {
    if (compareMode) {
      if (!photoLeft) {
        setPhotoLeft(photo);
        return;
      }
      if (photo.photo_id === photoLeft.photo_id) return;
      setPhotoRight(photo);
      setCompareMode(false);
      setFullscreenCompare(true);
      return;
    }
    setLightboxPhoto(photo);
  };

  const closeFullscreenCompare = () => {
    setFullscreenCompare(false);
    setPhotoLeft(null);
    setPhotoRight(null);
  };

  const swapPhotos = () => {
    setPhotoLeft(photoRight);
    setPhotoRight(photoLeft);
  };

  const canCompare = photoLeft && photoRight && photoLeft.photo_id !== photoRight.photo_id;

  if (photos.length === 0) {
    return (
      <div className="text-center py-12">
        <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-400 text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {photos.length >= 2 && (
          <Button
            type="button"
            size="sm"
            className={`rounded-full h-9 gap-1.5 ${compareMode ? "bg-slate-700 hover:bg-slate-800" : "bg-blue-600 hover:bg-blue-700"}`}
            onClick={() => (compareMode ? cancelCompareMode() : startCompareMode())}
          >
            <Columns2 className="w-3.5 h-3.5" />
            {compareMode ? "Annuler" : "Comparer"}
          </Button>
        )}
        <span className="text-xs text-slate-400 ml-auto">
          {photos.length} photo{photos.length > 1 ? "s" : ""}
        </span>
      </div>

      {compareMode && (
        <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-800">
          <span>
            {!photoLeft
              ? "① Cliquez sur la première photo — elle sera affichée à gauche"
              : "② Cliquez sur la deuxième photo — elle sera affichée à droite"}
          </span>
          <Button type="button" variant="ghost" size="sm" className="h-8 text-xs shrink-0" onClick={cancelCompareMode}>
            Annuler
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {sorted.map((p) => {
          const isLeft = photoLeft?.photo_id === p.photo_id;
          const isRight = photoRight?.photo_id === p.photo_id;
          const isSelected = isLeft || isRight;

          return (
            <div
              key={p.photo_id}
              className={`group relative rounded-xl overflow-hidden bg-slate-100 aspect-square cursor-pointer ring-offset-2 transition-all ${
                compareMode && isSelected
                  ? "ring-2 ring-blue-600"
                  : compareMode
                    ? "ring-2 ring-transparent hover:ring-blue-300"
                    : "hover:ring-2 hover:ring-blue-300"
              }`}
              onClick={() => handleGalleryClick(p)}
            >
              <img
                src={p.image_url}
                alt={p.caption || "Photo"}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-80" />
              <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                <Badge className="bg-white/90 text-slate-700 border-0 text-[10px]">
                  {formatPhotoDate(p.created_at)}
                </Badge>
                {isLeft && (
                  <Badge className="bg-slate-800 text-white border-0 text-[10px]">Gauche</Badge>
                )}
                {isRight && (
                  <Badge className="bg-blue-600 text-white border-0 text-[10px]">Droite</Badge>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-2 flex items-end justify-between gap-1">
                {p.caption ? (
                  <p className="text-[11px] text-white truncate flex-1">{p.caption}</p>
                ) : (
                  <span />
                )}
                {showDelete && onDelete && !compareMode && (
                  <button
                    type="button"
                    title="Supprimer"
                    className="w-7 h-7 rounded-full bg-red-500/90 text-white hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={(e) => { e.stopPropagation(); onDelete(p.photo_id); }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {!compareMode && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center">
                    <Maximize2 className="w-5 h-5 text-white" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          photos={sorted}
          onClose={(p) => setLightboxPhoto(p)}
        />
      )}

      {fullscreenCompare && canCompare && (
        <FullscreenSideCompare
          photoLeft={photoLeft}
          photoRight={photoRight}
          onClose={closeFullscreenCompare}
          onSwap={swapPhotos}
        />
      )}
    </div>
  );
}